import { useEffect } from 'react'
import { Avatar, NavLinks } from '@iveco-ti/iveco-ds'
import { ShieldBan } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { ROUTES } from '@/common/constants/routes'
import { useGetUserInfo } from '@/common/hooks/useGetUserInfo'
import { colorAvatarByName } from '@/common/utils/colorAvatarByName'

export function ProfileLayout() {
  const { t } = useTranslation('profile')
  const { userInfo } = useGetUserInfo()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const handleNavigate = (route: string) => {
    navigate(route)
  }

  const allowedApplications =
    userInfo?.UserPreference?.allowedApplications || []
  const availableApps = allowedApplications
    .map((allowedKey) => {
      const application = userInfo?.applications?.find(
        (app) => app.applicationKey === allowedKey,
      )
      return application ? application : null
    })
    .filter(Boolean)

  const hasApplications = availableApps.length > 0

  useEffect(() => {
    if (!hasApplications && pathname === ROUTES.PROFILE.ACCESS) {
      toast(Toast, {
        toastId: 'profile_access_denied',
        data: {
          type: 'danger',
          icon: ShieldBan,
          title: t('access_denied.title'),
          description: t('access_denied.description'),
        },
      })
      navigate(ROUTES.PROFILE.HOME)
    }
  }, [hasApplications, pathname, navigate, t])

  return (
    <>
      <NavLinks.Root>
        <div className="py-3 select-none hidden sm:flex items-center flex-row gap-3 mr-4">
          {userInfo && (
            <Avatar
              name={userInfo?.Person?.name}
              src={userInfo?.UserPreference?.avatarUrl || undefined}
              size="md"
              type="solid"
              color={colorAvatarByName(userInfo?.Person?.name)}
            />
          )}

          <span className="text-xl text-center rounded-lg text-brand-800 font-semibold">
            {t('links.profile')}
          </span>
        </div>

        <NavLinks.Link
          active={pathname === ROUTES.PROFILE.HOME}
          onClick={() => handleNavigate(ROUTES.PROFILE.HOME)}
        >
          {t('links.general_data')}
        </NavLinks.Link>

        <NavLinks.Link
          active={pathname === ROUTES.PROFILE.PREFERENCES}
          onClick={() => handleNavigate(ROUTES.PROFILE.PREFERENCES)}
        >
          {t('links.preferences')}
        </NavLinks.Link>

        <NavLinks.Link
          active={pathname === ROUTES.PROFILE.PASSWORD}
          onClick={() => handleNavigate(ROUTES.PROFILE.PASSWORD)}
        >
          {t('links.password')}
        </NavLinks.Link>

        {hasApplications && (
          <NavLinks.Link
            active={pathname === ROUTES.PROFILE.ACCESS}
            onClick={() => handleNavigate(ROUTES.PROFILE.ACCESS)}
          >
            {t('links.access')}
          </NavLinks.Link>
        )}
      </NavLinks.Root>

      <Outlet />
    </>
  )
}
